import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useQuery } from 'urql';
import {
  Period,
  MetaAdCampaignObjectiveInput,
  SocialAdsAnalyticsTabular_QueryFragment,
  SocialAdsAnalyticsNonTabular_QueryFragment,
} from '@frontend/gql/social/graphql';
import { getSocialAdsAnalyticsNonTabularData, getSocialAdsAnalyticsTabularData } from '../graphql';
import { TopAdData } from '../components/ads-table/ads-table-types';
import { SocialAccountData } from '../components/social-accounts-table/social-accounts-table-types';
import { filterAndSortAdsData, sortSocialAccountsData, AdCampaign } from './use-social-ads-data-utils';

interface UseAdsQueryDataProps {
  clientId?: string;
  currentIntervalRange: {
    startDate: string;
    endDate: string;
    precision: Period;
  };
  previousIntervalRange: {
    startDate: string;
    endDate: string;
  };
  startDate: string;
  endDate: string;
  fromOldData: boolean;
  adAccountIds?: string[];
  adCampaignIds?: string[]; // Separate field for campaigns
  adSetIds?: string[]; // Separate field for ad sets
  adCampaignObjectives?: string[];
  memberIds?: string[];
  compareWithPartnershipAds?: boolean;
  tab: string;
  adCampaigns?: AdCampaign[]; // Add adCampaigns for splitting IDs
  isInitialized?: boolean; // Add initialization guard
}

export const useAdsQueryData = ({
  clientId,
  currentIntervalRange,
  previousIntervalRange,
  startDate,
  endDate,
  fromOldData,
  adAccountIds,
  adCampaignIds,
  adSetIds,
  adCampaignObjectives,
  memberIds,
  compareWithPartnershipAds: _compareWithPartnershipAds,
  tab,
  isInitialized = true, // Default to true for backward compatibility
}: UseAdsQueryDataProps) => {
  // Track previous values to detect changes
  const prevValues = useRef({
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignIds,
    adSetIds,
    adCampaignObjectives,
    memberIds,
    compareWithPartnershipAds: _compareWithPartnershipAds,
  });

  const [refetching, setRefetching] = useState(false);

  // Use separate adCampaignIds and adSetIds
  const finalAdCampaignIds = useMemo(() => {
    return adCampaignIds && adCampaignIds.length > 0 ? adCampaignIds : undefined;
  }, [adCampaignIds]);

  const finalAdSetIds = useMemo(() => {
    return adSetIds && adSetIds.length > 0 ? adSetIds : undefined;
  }, [adSetIds]);

  const finalAdCampaignObjectives = useMemo(() => {
    if (!adCampaignObjectives || adCampaignObjectives.length === 0) return undefined;
    const allowed: readonly MetaAdCampaignObjectiveInput[] = [
      'OUTCOME_APP_PROMOTION',
      'OUTCOME_AWARENESS',
      'OUTCOME_ENGAGEMENT',
      'OUTCOME_LEADS',
      'OUTCOME_SALES',
      'OUTCOME_TRAFFIC',
    ];
    const set = new Set(allowed);
    const filtered = adCampaignObjectives.filter((v): v is MetaAdCampaignObjectiveInput =>
      set.has(v as MetaAdCampaignObjectiveInput),
    );
    return filtered.length > 0 ? filtered : undefined;
  }, [adCampaignObjectives]);

  // Execute the GraphQL query for graph data (with current precision)
  const [graphResult, executeGraphQuery] = useQuery({
    query: getSocialAdsAnalyticsNonTabularData,
    variables: {
      clientId: clientId || '',
      currentIntervalRange: {
        startDate: currentIntervalRange.startDate,
        endDate: currentIntervalRange.endDate,
        precision: currentIntervalRange.precision,
      },
      previousIntervalRange: {
        startDate: previousIntervalRange.startDate,
        endDate: previousIntervalRange.endDate,
      },
      fromOldData,
      adAccountIds: adAccountIds && adAccountIds.length > 0 ? adAccountIds : undefined,
      ...(finalAdCampaignIds && { adCampaignIds: finalAdCampaignIds }),
      ...(finalAdSetIds && { adSetIds: finalAdSetIds }),
      ...(finalAdCampaignObjectives && { adCampaignObjectives: finalAdCampaignObjectives }),
      ...(memberIds && { memberIds }),
    },
    pause: !clientId || tab !== 'overview' || !isInitialized,
  });

  // Execute a separate query for table data with precision: 'ALL'
  const [tableResult, executeTableQuery] = useQuery({
    query: getSocialAdsAnalyticsTabularData,
    variables: {
      clientId: clientId || '',
      currentIntervalRange: {
        startDate: currentIntervalRange.startDate,
        endDate: currentIntervalRange.endDate,
        precision: 'ALL' as Period, // Force precision to ALL for tabular data
      },
      adAccountIds: adAccountIds && adAccountIds.length > 0 ? adAccountIds : undefined,
      ...(finalAdCampaignIds && { adCampaignIds: finalAdCampaignIds }),
      ...(finalAdSetIds && { adSetIds: finalAdSetIds }),
      ...(finalAdCampaignObjectives && { adCampaignObjectives: finalAdCampaignObjectives }),
    },
    pause: !clientId || tab !== 'overview' || !isInitialized,
  });

  const { data: graphData, fetching: graphFetching, error: graphError } = graphResult;
  const { data: tableData, fetching: tableFetching, error: tableError } = tableResult;

  // Use graphData?.client for graph-related data
  const graphClient = (graphData as SocialAdsAnalyticsNonTabular_QueryFragment)?.client;

  // Use tableData?.client for table-related data (topAds and topSocialAccounts)
  const tableClient = (tableData as SocialAdsAnalyticsTabular_QueryFragment)?.client;

  // Check if parameters have changed
  const hasParamsChanged = useMemo(() => {
    const current = {
      clientId,
      currentIntervalRange: JSON.stringify(currentIntervalRange),
      previousIntervalRange: JSON.stringify(previousIntervalRange),
      startDate,
      endDate,
      fromOldData,
      adAccountIds: JSON.stringify(adAccountIds?.sort()),
      adCampaignIds: JSON.stringify(adCampaignIds?.sort()),
      adSetIds: JSON.stringify(adSetIds?.sort()),
      adCampaignObjectives: JSON.stringify(adCampaignObjectives?.sort()),
      memberIds: JSON.stringify(memberIds?.sort()),
      compareWithPartnershipAds: _compareWithPartnershipAds,
    };

    const previous = {
      clientId: prevValues.current.clientId,
      currentIntervalRange: JSON.stringify(prevValues.current.currentIntervalRange),
      previousIntervalRange: JSON.stringify(prevValues.current.previousIntervalRange),
      startDate: prevValues.current.startDate,
      endDate: prevValues.current.endDate,
      fromOldData: prevValues.current.fromOldData,
      adAccountIds: JSON.stringify(prevValues.current.adAccountIds?.sort()),
      adCampaignIds: JSON.stringify(prevValues.current.adCampaignIds?.sort()),
      adSetIds: JSON.stringify(prevValues.current.adSetIds?.sort()),
      adCampaignObjectives: JSON.stringify(prevValues.current.adCampaignObjectives?.sort()),
      memberIds: JSON.stringify(prevValues.current.memberIds?.sort()),
      compareWithPartnershipAds: prevValues.current.compareWithPartnershipAds,
    };

    return JSON.stringify(current) !== JSON.stringify(previous);
  }, [
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignIds,
    adSetIds,
    adCampaignObjectives,
    memberIds,
    _compareWithPartnershipAds,
  ]);

  // Update previous values when parameters change
  useEffect(() => {
    if (hasParamsChanged) {
      prevValues.current = {
        clientId,
        currentIntervalRange,
        previousIntervalRange,
        startDate,
        endDate,
        fromOldData,
        adAccountIds,
        adCampaignIds,
        adSetIds,
        adCampaignObjectives,
        memberIds,
        compareWithPartnershipAds: _compareWithPartnershipAds,
      };
    }
  }, [
    hasParamsChanged,
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate,
    endDate,
    fromOldData,
    adAccountIds,
    adCampaignIds,
    adSetIds,
    adCampaignObjectives,
    memberIds,
    _compareWithPartnershipAds,
  ]);

  // Manual refetch function with loading state
  const handleRefetch = useCallback(async () => {
    setRefetching(true);
    try {
      await Promise.all([
        executeGraphQuery({ requestPolicy: 'network-only' }),
        executeTableQuery({ requestPolicy: 'network-only' }),
      ]);
    } finally {
      setRefetching(false);
    }
  }, [executeGraphQuery, executeTableQuery]);

  // transform topAds to TopAdData[] using table data with precision: 'ALL'
  const topAds: TopAdData[] = useMemo(() => {
    const adsData = tableClient?.sumInsightsBy || [];
    const sortedAds = filterAndSortAdsData(adsData);
    return sortedAds.slice(0, 5); // Top 5 ads for overview
  }, [tableClient]);

  // transform social accounts data to SocialAccountData[] using table data with precision: 'ALL'
  const topSocialAccounts: SocialAccountData[] = useMemo(() => {
    const socialAccountsData = tableClient?.sumInsightsBySocialAccounts || [];
    const sortedData = sortSocialAccountsData(socialAccountsData);
    return sortedData.slice(0, 5); // Top 5 social accounts for overview
  }, [tableClient]);

  // Process the fragment data for easier consumption
  const processedData = useMemo(() => {
    // Use graphClient for graph-related data and tableClient for table data
    return {
      metaAdAccounts: graphClient?.metaAdAccounts || [],
      currentInterval: graphClient?.currentInterval || null,
      currentIntervalsIntervalInsights: graphClient?.currentIntervalsIntervalInsights || [],
      currentIntervalsIntervalInsightsNotAspireSourced:
        graphClient?.currentIntervalsIntervalInsightsNotAspireSourced || [],
      previousInterval: graphClient?.previousInterval || null,
      currentIntervalNotAspireSourced: graphClient?.currentIntervalNotAspireSourced || null,
      previousIntervalNotAspireSourced: graphClient?.previousIntervalNotAspireSourced || null,
      // Real data from GraphQL query with precision: 'ALL' for tables
      topAds,
      topSocialAccounts,
    };
  }, [graphClient, topAds, topSocialAccounts]);

  return {
    fetching: graphFetching || tableFetching,
    refetching,
    error: graphError || tableError,
    processedData,
    handleRefetch,
  };
};
